import React, { useMemo } from 'react';
import Box from '@elementor/ui/Box';
import Container from '@elementor/ui/Container';
import Typography from '@elementor/ui/Typography';
import Button from '@elementor/ui/Button';
import Card from '@elementor/ui/Card';
import CardContent from '@elementor/ui/CardContent';

import Skeleton from '@elementor/ui/Skeleton';
import { AlertTriangleIcon } from '@elementor/icons';
import SearchInput from './SearchInput';
import AISummaryCard from './AISummaryCard';
import ArticleCard from './ArticleCard';

function LoadingSkeleton() {
  return (
    <Card sx={{ mb: 3, borderRadius: 4, flex: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={120} height={24} />
        </Box>
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="95%" height={20} />
        <Skeleton variant="text" width="100%" height={20} sx={{ mt: 2 }} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <Skeleton variant="text" width="85%" height={20} sx={{ mt: 2 }} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="70%" height={20} />
      </CardContent>
    </Card>
  );
}

/**
 * Merge citations and related_articles into a single deduped list.
 * Citations come first so they win the dedupe if the same link appears
 * in both (shouldn't happen per the agent prompt, but defensive).
 */
function mergeArticles(response) {
  const citations = response?.citations || [];
  const related = response?.related_articles || [];
  const seen = new Set();
  const out = [];
  for (const a of [...citations, ...related]) {
    if (!a?.link || seen.has(a.link)) continue;
    seen.add(a.link);
    out.push(a);
  }
  return out;
}

function ResultsPage({
  query,
  response,
  onNewSearch,
  onBackToSearch,
  isLoading,
  error,
}) {
  const articles = useMemo(() => mergeArticles(response), [response]);

  // `videos` derivation moved into AISummaryCard now that the card owns
  // the full response object (needed for the feedback POST payload).
  // See `_retrieve_videos` in agent/app/runtime.py for the source shape.

  const answer = response?.answer || '';

  const handleResultsSearch = (text) => {
    onNewSearch(text, 'results');
  };

  return (
    <Box className="ft-results-wrapper" sx={{ backgroundColor: 'background.paper', minHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Search */}
      <Box
        className="ft-results-header"
        component="header"
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ width: '100%' }}>
            <SearchInput
              onSubmit={handleResultsSearch}
              placeholder="Search for help..."
              isLoading={isLoading}
              initialValue={query}
              showSubmitButton
              buttonText="Search"
            />
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Box
        className="ft-results-content"
        component="main"
        aria-live="polite"
        aria-busy={isLoading ? 'true' : 'false'}
        sx={{ py: 4, flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {isLoading && <LoadingSkeleton />}

          {error && !isLoading && (
            <Card
              sx={{
                mb: 3,
                borderRadius: 4,
                textAlign: 'center',
                py: 4,
              }}
            >
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <AlertTriangleIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Something went wrong
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {error}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onNewSearch(query)}
                  >
                    Try again
                  </Button>
                  <Button variant="text" onClick={onBackToSearch}>
                    Back to search
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {!isLoading && !error && answer && (
            <AISummaryCard response={response} query={query} />
          )}

          {!isLoading && !error && articles.length > 0 && (
            <Box className="ft-more-results" sx={{ mt: 3 }}>
              <Typography
                className="ft-more-results-title"
                variant="h6"
                fontWeight={700}
                sx={{ mb: 3 }}
              >
                More results
              </Typography>
              <Box className="ft-more-results-list" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {articles.map((article) => (
                  <ArticleCard
                    key={article.link}
                    title={article.title}
                    url={article.link}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Container>
      </Box>
    </Box>
  );
}

export default ResultsPage;
